import React, { useState } from 'react';
import axios from 'axios';
import { contactInfo, companyInfo, navLinks, socialLinks } from '../data/mock';
import { Mail, MapPin, Send, Linkedin, Phone } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { toast } from '../hooks/use-toast';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Contact = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const scrollToSection = (href) => {
    console.log('Footer link clicked:', href);
    
    // Handle home - scroll to top
    if (href === '#home') {
      window.scrollTo({ top: 0, behavior: 'smooth' });
      console.log('Scrolling to top');
      return;
    }
    
    // Get the section ID (remove # if present)
    const sectionId = href.startsWith('#') ? href.substring(1) : href;
    const element = document.getElementById(sectionId);
    
    console.log('Trying to scroll to:', sectionId, 'Found element:', !!element);
    
    if (element) {
      // Scroll to the section
      const offsetTop = element.offsetTop - 80; // Account for fixed navbar
      window.scrollTo({
        top: offsetTop,
        behavior: 'smooth'
      });
      console.log('Scrolled to:', sectionId);
    } else {
      console.error('Section not found:', sectionId);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    
    try {
      await axios.post(`${API}/contact`, {
        name: formData.name,
        email: formData.email,
        message: formData.message
      });
      
      toast({
        title: "Message Sent",
        description: "Thank you for your inquiry. We will respond shortly.",
      });
      
      setFormData({ name: '', email: '', message: '' });
    } catch (error) {
      console.error('Error submitting form:', error);
      toast({
        title: "Error",
        description: "Failed to send message. Please try again.",
        variant: "destructive"
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <section id="contact" className="py-32 lg:py-40 bg-[#0f1c2e] relative overflow-hidden">
      {/* Background Pattern */}
      <div className="absolute inset-0 opacity-[0.02]" style={{
        backgroundImage: `linear-gradient(rgba(255,255,255,0.05) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.05) 1px, transparent 1px)`,
        backgroundSize: '100px 100px'
      }} />
      
      {/* Enhanced Animated Overlays */}
      <div className="absolute inset-0 bg-gradient-to-br from-[#D4AF37]/8 via-transparent to-transparent animate-gradient-contact-1" />
      <div className="absolute inset-0 bg-gradient-to-t from-transparent via-[#D4AF37]/4 to-transparent animate-gradient-contact-2" />
      
      {/* Enhanced Floating Particles */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute top-[20%] right-[25%] w-2 h-2 bg-[#D4AF37]/50 rounded-full animate-float-contact-1 blur-sm" />
        <div className="absolute top-[45%] left-[30%] w-1.5 h-1.5 bg-white/40 rounded-full animate-float-contact-2 blur-sm" />
        <div className="absolute bottom-[30%] right-[35%] w-1.5 h-1.5 bg-[#D4AF37]/45 rounded-full animate-float-contact-3 blur-sm" />
        <div className="absolute top-[60%] left-[20%] w-1 h-1 bg-white/50 rounded-full animate-float-contact-4 blur-sm" />
        <div className="absolute bottom-[45%] right-[15%] w-2 h-2 bg-[#D4AF37]/55 rounded-full animate-float-contact-5 blur-sm" />
        <div className="absolute top-[35%] left-[50%] w-1 h-1 bg-white/45 rounded-full animate-float-contact-6 blur-sm" />
        <div className="absolute bottom-[20%] left-[45%] w-1.5 h-1.5 bg-[#D4AF37]/40 rounded-full animate-float-contact-7 blur-sm" />
        <div className="absolute top-[70%] right-[50%] w-1 h-1 bg-white/55 rounded-full animate-float-contact-8 blur-sm" />
      </div>
      
      {/* Smooth Light Beams */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute top-0 left-[30%] w-[1px] h-full bg-gradient-to-b from-transparent via-[#D4AF37]/15 to-transparent animate-beam-contact-1" />
        <div className="absolute top-0 right-[35%] w-[1px] h-full bg-gradient-to-b from-transparent via-white/10 to-transparent animate-beam-contact-2" />
      </div>
      
      {/* Pulsing Ambient Orbs */}
      <div className="absolute top-1/4 left-1/6 w-96 h-96 bg-[#D4AF37]/10 rounded-full blur-3xl animate-pulse-ambient-contact-1" />
      <div className="absolute bottom-1/5 right-1/5 w-80 h-80 bg-[#D4AF37]/8 rounded-full blur-3xl animate-pulse-ambient-contact-2" />
      
      {/* Decorative Elements */}
      <div className="absolute top-0 left-0 w-1/3 h-[1px] bg-gradient-to-r from-[#D4AF37] to-transparent animate-line-glow" />
      <div className="absolute top-0 right-0 w-1/3 h-[1px] bg-gradient-to-l from-[#D4AF37] to-transparent animate-line-glow" />
      
      <div className="relative z-10 max-w-7xl mx-auto px-6 lg:px-8">
        {/* Header */}
        <div className="text-center mb-20">
          <div className="flex items-center justify-center gap-6 mb-8">
            <div className="w-16 h-[1px] bg-[#D4AF37]" />
            <span className="text-[#D4AF37] text-xs font-medium tracking-[0.3em] uppercase">Contact</span>
            <div className="w-16 h-[1px] bg-[#D4AF37]" />
          </div>
          <h2 className="font-serif text-4xl lg:text-5xl xl:text-6xl text-white mb-6">
            Let's Discuss Your<br />
            <span className="text-[#D4AF37]">Investment Goals</span>
          </h2>
          <p className="text-white/50 text-lg max-w-2xl mx-auto">
            Connect with our experienced team to explore opportunities.
          </p>
        </div>

        <div className="grid lg:grid-cols-5 gap-16">
          {/* Contact Form */}
          <div className="lg:col-span-3">
            <div className="bg-white p-12">
              <h3 className="text-[#0f1c2e] text-xs tracking-[0.3em] uppercase mb-10">Send an Inquiry</h3>
              <form onSubmit={handleSubmit} className="space-y-8">
                <div className="grid md:grid-cols-2 gap-8">
                  <div>
                    <label className="block text-[#0f1c2e]/50 text-xs tracking-[0.15em] uppercase mb-3">Full Name</label>
                    <Input
                      type="text"
                      name="name"
                      value={formData.name}
                      onChange={handleInputChange}
                      placeholder="Your name"
                      required
                      className="border-0 border-b border-[#0f1c2e]/20 rounded-none px-0 h-12 focus:border-[#D4AF37] focus:ring-0 transition-colors"
                    />
                  </div>
                  <div>
                    <label className="block text-[#0f1c2e]/50 text-xs tracking-[0.15em] uppercase mb-3">Email Address</label>
                    <Input
                      type="email"
                      name="email"
                      value={formData.email}
                      onChange={handleInputChange}
                      placeholder="your@email.com"
                      required
                      className="border-0 border-b border-[#0f1c2e]/20 rounded-none px-0 h-12 focus:border-[#D4AF37] focus:ring-0 transition-colors"
                    />
                  </div>
                </div>
                <div>
                  <label className="block text-[#0f1c2e]/50 text-xs tracking-[0.15em] uppercase mb-3">Message</label>
                  <Textarea
                    name="message"
                    value={formData.message}
                    onChange={handleInputChange}
                    placeholder="How can we assist you?"
                    rows={4}
                    required
                    className="border-0 border-b border-[#0f1c2e]/20 rounded-none px-0 focus:border-[#D4AF37] focus:ring-0 resize-none transition-colors"
                  />
                </div>
                <Button
                  type="submit"
                  disabled={isSubmitting}
                  className="w-full bg-[#0f1c2e] hover:bg-[#1a2d47] text-white py-7 text-xs font-semibold tracking-[0.2em] uppercase disabled:opacity-50 transition-all duration-500"
                >
                  {isSubmitting ? (
                    <span className="flex items-center justify-center">
                      <svg className="animate-spin -ml-1 mr-3 h-4 w-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                      </svg>
                      Sending...
                    </span>
                  ) : (
                    <span className="flex items-center justify-center">
                      Send Message
                      <Send className="ml-3 w-4 h-4" />
                    </span>
                  )}
                </Button>
              </form>
            </div>
          </div>

          {/* Contact Info */}
          <div className="lg:col-span-2 space-y-6">
            <div className="border border-white/10 p-10 hover:border-[#D4AF37]/30 transition-colors duration-500">
              <Mail className="w-6 h-6 text-[#D4AF37] mb-6" />
              <div className="text-white/40 text-xs tracking-[0.15em] uppercase mb-2">Email</div>
              <div className="text-white text-lg">{contactInfo.email}</div>
            </div>

            <div className="border border-white/10 p-10 hover:border-[#D4AF37]/30 transition-colors duration-500">
              <MapPin className="w-6 h-6 text-[#D4AF37] mb-6" />
              <div className="text-white/40 text-xs tracking-[0.15em] uppercase mb-2">Australian Office</div>
              <div className="text-white text-lg leading-relaxed">{contactInfo.address}</div>
            </div>

            <a 
              href={socialLinks.linkedin} 
              target="_blank" 
              rel="noopener noreferrer"
              className="flex items-center gap-6 border border-white/10 p-10 hover:border-[#D4AF37]/30 transition-colors duration-500 group"
            >
              <Linkedin className="w-6 h-6 text-[#D4AF37]" />
              <div>
                <div className="text-white/40 text-xs tracking-[0.15em] uppercase mb-1">Connect on</div>
                <div className="text-white text-lg group-hover:text-[#D4AF37] transition-colors">LinkedIn</div>
              </div>
            </a>
          </div>
        </div>
      </div>
      
      {/* CSS Animations */}
      <style jsx>{`
        @keyframes gradient-contact-1 {
          0%, 100% { opacity: 0.08; }
          50% { opacity: 0.15; }
        }

        @keyframes gradient-contact-2 {
          0%, 100% { transform: translateY(0); opacity: 0.04; }
          50% { transform: translateY(-25px); opacity: 0.10; }
        }

        @keyframes float-contact-1 {
          0%, 100% { transform: translate(0, 0) scale(1); opacity: 0.5; }
          50% { transform: translate(55px, -65px) scale(1.3); opacity: 0.8; }
        }

        @keyframes float-contact-2 {
          0%, 100% { transform: translate(0, 0); opacity: 0.4; }
          50% { transform: translate(-60px, 75px); opacity: 0.7; }
        }

        @keyframes float-contact-3 {
          0%, 100% { transform: translate(0, 0) rotate(0deg); opacity: 0.45; }
          50% { transform: translate(50px, -55px) rotate(180deg); opacity: 0.75; }
        }

        @keyframes float-contact-4 {
          0%, 100% { transform: translate(0, 0); opacity: 0.5; }
          50% { transform: translate(-45px, -60px); opacity: 0.8; }
        }

        @keyframes float-contact-5 {
          0%, 100% { transform: translate(0, 0) scale(1); opacity: 0.55; }
          50% { transform: translate(65px, 70px) scale(1.35); opacity: 0.85; }
        }

        @keyframes float-contact-6 {
          0%, 100% { transform: translate(0, 0); opacity: 0.45; }
          50% { transform: translate(-50px, 60px); opacity: 0.75; }
        }

        @keyframes float-contact-7 {
          0%, 100% { transform: translate(0, 0) rotate(0deg); opacity: 0.4; }
          50% { transform: translate(58px, -68px) rotate(200deg); opacity: 0.7; }
        }

        @keyframes float-contact-8 {
          0%, 100% { transform: translate(0, 0) scale(1); opacity: 0.55; }
          50% { transform: translate(-55px, -65px) scale(1.25); opacity: 0.8; }
        }

        @keyframes beam-contact-1 {
          0%, 100% { opacity: 0; transform: translateY(-100%); }
          50% { opacity: 0.15; transform: translateY(100%); }
        }

        @keyframes beam-contact-2 {
          0%, 100% { opacity: 0; transform: translateY(100%); }
          50% { opacity: 0.10; transform: translateY(-100%); }
        }

        @keyframes pulse-ambient-contact-1 {
          0%, 100% { opacity: 0.10; transform: scale(1); }
          50% { opacity: 0.18; transform: scale(1.35); }
        }

        @keyframes pulse-ambient-contact-2 {
          0%, 100% { opacity: 0.08; transform: scale(1); }
          50% { opacity: 0.15; transform: scale(1.4); }
        }

        @keyframes line-glow {
          0%, 100% { opacity: 0.8; }
          50% { opacity: 1; }
        }

        .animate-gradient-contact-1 { animation: gradient-contact-1 12s ease-in-out infinite; }
        .animate-gradient-contact-2 { animation: gradient-contact-2 18s ease-in-out infinite; }
        .animate-float-contact-1 { animation: float-contact-1 20s cubic-bezier(0.45, 0.05, 0.55, 0.95) infinite; }
        .animate-float-contact-2 { animation: float-contact-2 22s cubic-bezier(0.45, 0.05, 0.55, 0.95) infinite; }
        .animate-float-contact-3 { animation: float-contact-3 24s cubic-bezier(0.45, 0.05, 0.55, 0.95) infinite; }
        .animate-float-contact-4 { animation: float-contact-4 21s cubic-bezier(0.45, 0.05, 0.55, 0.95) infinite; }
        .animate-float-contact-5 { animation: float-contact-5 26s cubic-bezier(0.45, 0.05, 0.55, 0.95) infinite; }
        .animate-float-contact-6 { animation: float-contact-6 23s cubic-bezier(0.45, 0.05, 0.55, 0.95) infinite; }
        .animate-float-contact-7 { animation: float-contact-7 25s cubic-bezier(0.45, 0.05, 0.55, 0.95) infinite; }
        .animate-float-contact-8 { animation: float-contact-8 27s cubic-bezier(0.45, 0.05, 0.55, 0.95) infinite; }
        .animate-beam-contact-1 { animation: beam-contact-1 17s ease-in-out infinite; }
        .animate-beam-contact-2 { animation: beam-contact-2 19s ease-in-out infinite; }
        .animate-pulse-ambient-contact-1 { animation: pulse-ambient-contact-1 11s ease-in-out infinite; }
        .animate-pulse-ambient-contact-2 { animation: pulse-ambient-contact-2 13s ease-in-out infinite; }
        .animate-line-glow { animation: line-glow 3s ease-in-out infinite; }
      `}</style>
    </section>
  );
};

// Footer Component
export const Footer = () => {
  const scrollToSection = (href) => {
    console.log('Footer link clicked:', href);
    
    // Handle home - scroll to top
    if (href === '#home') {
      window.scrollTo({ top: 0, behavior: 'smooth' });
      return;
    }
    
    // Get the section ID
    const sectionId = href.startsWith('#') ? href.substring(1) : href;
    const element = document.getElementById(sectionId);
    
    if (element) {
      const offsetTop = element.offsetTop - 80;
      window.scrollTo({
        top: offsetTop,
        behavior: 'smooth'
      });
    }
  };

  return (
    <footer className="bg-[#080e18] py-20">
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        <div className="grid md:grid-cols-4 gap-12 mb-16" style={{pointerEvents: 'none'}}>
          {/* Logo & Description */}
          <div className="md:col-span-2" style={{pointerEvents: 'auto'}}>
            <div className="flex items-center space-x-4 mb-6">
              <div className="w-12 h-12 bg-[#0f1c2e] border border-[#D4AF37]/30 flex items-center justify-center">
                <span className="text-[#D4AF37] font-serif font-bold text-xl">R</span>
              </div>
              <div>
                <span className="text-lg font-semibold text-white block tracking-wide">
                  {companyInfo.name}
                </span>
                <span className="text-white/30 text-xs tracking-wide">
                  Rosevilla Capital Pty Ltd | ABN 60 682 305 166
                </span>
              </div>
            </div>
            <p className="text-white/40 leading-relaxed max-w-md mb-8 text-sm">
              A premier investment holding company with over $4 billion collectively deployed by our team in global markets across India, Asia, and Australia.
            </p>
            <a 
              href={socialLinks.linkedin} 
              target="_blank" 
              rel="noopener noreferrer"
              className="inline-flex items-center gap-3 text-white/60 hover:text-[#D4AF37] transition-colors text-sm font-medium"
              style={{pointerEvents: 'auto', cursor: 'pointer'}}
            >
              <Linkedin className="w-4 h-4" />
              <span>Follow us on LinkedIn</span>
            </a>
          </div>

          {/* Quick Links */}
          <div style={{pointerEvents: 'auto'}}>
            <h4 className="text-white text-xs tracking-[0.2em] uppercase mb-8">QUICK LINKS</h4>
            <ul className="space-y-5">
              <li>
                <a href="#home" onClick={(e) => { e.preventDefault(); window.scrollTo({ top: 0, behavior: 'smooth' }); }} className="text-white hover:text-[#D4AF37] transition-colors duration-200 text-base font-medium cursor-pointer inline-block" style={{pointerEvents: 'auto'}}>Home</a>
              </li>
              <li>
                <a href="#about" onClick={(e) => { e.preventDefault(); const el = document.getElementById('about'); if (el) window.scrollTo({ top: el.offsetTop - 80, behavior: 'smooth' }); }} className="text-white hover:text-[#D4AF37] transition-colors duration-200 text-base font-medium cursor-pointer inline-block" style={{pointerEvents: 'auto'}}>About</a>
              </li>
              <li>
                <a href="#services" onClick={(e) => { e.preventDefault(); const el = document.getElementById('services'); if (el) window.scrollTo({ top: el.offsetTop - 80, behavior: 'smooth' }); }} className="text-white hover:text-[#D4AF37] transition-colors duration-200 text-base font-medium cursor-pointer inline-block" style={{pointerEvents: 'auto'}}>Services</a>
              </li>
              <li>
                <a href="#story" onClick={(e) => { e.preventDefault(); const el = document.getElementById('story'); if (el) window.scrollTo({ top: el.offsetTop - 80, behavior: 'smooth' }); }} className="text-white hover:text-[#D4AF37] transition-colors duration-200 text-base font-medium cursor-pointer inline-block" style={{pointerEvents: 'auto'}}>Our Story</a>
              </li>
              <li>
                <a href="#contact" onClick={(e) => { e.preventDefault(); const el = document.getElementById('contact'); if (el) window.scrollTo({ top: el.offsetTop - 80, behavior: 'smooth' }); }} className="text-white hover:text-[#D4AF37] transition-colors duration-200 text-base font-medium cursor-pointer inline-block" style={{pointerEvents: 'auto'}}>Contact</a>
              </li>
            </ul>
          </div>

          {/* Contact */}
          <div style={{pointerEvents: 'auto'}}>
            <h4 className="text-white text-xs tracking-[0.2em] uppercase mb-8">Contact</h4>
            <ul className="space-y-4 text-white/40 text-sm">
              <li>{contactInfo.email}</li>
              <li className="leading-relaxed">{contactInfo.address}</li>
            </ul>
          </div>
        </div>

        {/* Bottom Bar */}
        <div className="pt-10 border-t border-white/5 flex flex-col md:flex-row justify-between items-center gap-6">
          <p className="text-white/20 text-sm">
            © 2025 Rosevilla Group. All rights reserved.
          </p>
          <div className="flex gap-10 text-white/20 text-sm">
            <a href="#" className="hover:text-[#D4AF37] transition-colors">Privacy Policy</a>
            <a href="#" className="hover:text-[#D4AF37] transition-colors">Terms of Service</a>
            <a href="#" className="hover:text-[#D4AF37] transition-colors">Disclaimer</a>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Contact;
